import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.jsx';
import Logo from '../ui/Logo';

/**
 * Protected Route Component
 * Redirects unauthenticated users to login page
 * Optionally checks for specific capabilities or profile completion
 */
const ProtectedRoute = ({ 
  children, 
  requireCapability = null,
  requireProfileComplete = false,
  fallbackPath = '/login'
}) => {
  const { isAuthenticated, loading, user, hasCapability, isProfileComplete } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-6">
            <Logo variant="full" size="large" alt="Crowblock" />
          </div>
          <div className="w-12 h-12 border-4 border-primary-teal/30 border-t-primary-teal rounded-full animate-spin mx-auto mb-4" />
          <p className="text-neutral-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate 
        to="/login" 
        state={{ from: location }} 
        replace 
      />
    );
  }

  // Check if specific capability is required
  if (requireCapability && !hasCapability(requireCapability)) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <Logo variant="full" size="medium" alt="Crowblock" />
            </div>
            <div className="w-16 h-16 bg-secondary-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-secondary-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">
              Access Restricted
            </h2>
            <p className="text-neutral-600 mb-6">
              You don't have permission to access this page. 
              {requireCapability === 'create_project' && ' Complete your profile to create projects.'}
              {requireCapability === 'approve_project' && ' Admin access required.'}
              {requireCapability === 'manage_admins' && ' Super admin access required.'}
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-2 bg-primary-teal text-white rounded-lg hover:bg-primary-teal/90 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if profile completion is required
  if (requireProfileComplete && !isProfileComplete()) {
    return (
      <Navigate 
        to="/profile/complete" 
        state={{ from: location }} 
        replace 
      />
    );
  }

  // Render protected content
  return children;
};

export default ProtectedRoute;
